/* assets/cruise-map.js
   Reusable Cruise Map renderer for Google Maps Platform

   Usage from Liquid/PageFly:
   window.CruiseMap.render('#elemId', {
     apiKey: 'YOUR_KEY',
     mapId: 'YOUR_MAP_ID',
     animate: false,          // turn off if you just want static arrows
     shipSpeedKmh: 37,
     itinerary: [ {day:1,name:'Miami',lat:25.77,lng:-80.19}, ... ]
   })
*/
window.CruiseMap = (function () {
  const state = {
    gmapsPromise: null,
    loadedKey: null,
  };

  // ---------------------------------------------------------------
  // Load Google Maps
  // ---------------------------------------------------------------
  function loadGoogleMaps(apiKey) {
    if (window.google && window.google.maps) return Promise.resolve();
    if (state.gmapsPromise && state.loadedKey === apiKey) return state.gmapsPromise;

    state.loadedKey = apiKey;
    state.gmapsPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=geometry`;
      s.async = true;
      s.defer = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Google Maps failed to load'));
      document.head.appendChild(s);
    });
    return state.gmapsPromise;
  }

  // ---------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------
  function metersPerSecond(kmh) {
    return (kmh * 1000) / 3600;
  }

  function buildLatLngPath(itinerary) {
    return itinerary.map(p => new google.maps.LatLng(p.lat, p.lng));
  }

  // ---------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------
  async function render(selectorOrEl, opts) {
    const el = typeof selectorOrEl === 'string' ? document.querySelector(selectorOrEl) : selectorOrEl;
    if (!el) throw new Error('CruiseMap: container not found');

    const {
      apiKey,
      mapId = null,
      animate = false,
      shipSpeedKmh = 37,
      itinerary = [],
      styles = null,
      initialCenter = null,
      initialZoom = 5,
    } = opts || {};

    if (!apiKey) throw new Error('CruiseMap: apiKey is required');
    if (!Array.isArray(itinerary) || itinerary.length < 2) throw new Error('CruiseMap: itinerary needs ≥ 2 stops');

    await loadGoogleMaps(apiKey);

    // Create map
    const map = new google.maps.Map(el, {
      center: initialCenter || { lat: itinerary[0].lat, lng: itinerary[0].lng },
      zoom: initialZoom,
      mapId: mapId || undefined,
      styles: mapId ? undefined : styles || null,
      gestureHandling: 'none' // disable scroll zoom unless ctrl/cmd
    });

    // Enable zoom only with Ctrl/Cmd key
    map.getDiv().addEventListener('wheel', function (e) {
      if (e.ctrlKey || e.metaKey) {
        map.setOptions({ gestureHandling: 'auto' });
      } else {
        map.setOptions({ gestureHandling: 'none' });
      }
    });

    // Fit to bounds
    const bounds = new google.maps.LatLngBounds();
    const path = buildLatLngPath(itinerary);
    path.forEach(ll => bounds.extend(ll));
    map.fitBounds(bounds, 64);

    // ---------------------------------------------------------------
    // Port markers
    // ---------------------------------------------------------------
    const info = new google.maps.InfoWindow();
    itinerary.forEach(stop => {
      const marker = new google.maps.Marker({
        position: { lat: stop.lat, lng: stop.lng },
        map,
        title: stop.name ? `Day ${stop.day}: ${stop.name}` : `Day ${stop.day}`,
        label: { text: String(stop.day || ''), color: '#ffffff', fontWeight: '700' },
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: '#0b62d6',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2
        }
      });

      const dateLine = stop.date ? `<div><strong>Date:</strong> ${stop.date}</div>` : '';
      const noteLine = stop.note ? `<div>${stop.note}</div>` : '';
      const html = `
        <div style="font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica Neue,Arial;max-width:260px;">
          <div style="font-size:14px;line-height:1.3;margin-bottom:6px;"><strong>Day ${stop.day || ''} – ${stop.name || ''}</strong></div>
          ${dateLine}${noteLine}
        </div>`;
      marker.addListener('click', () => {
        info.setContent(html);
        info.open(map, marker);
      });
    });

    // ---------------------------------------------------------------
    // Route lines + arrows (no arrows on markers)
    // ---------------------------------------------------------------
    const baseRoute = new google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: '#0b62d6',
      strokeOpacity: 0.9,
      strokeWeight: 3
    });
    baseRoute.setMap(map);

    const arrowSymbol = {
      path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      scale: 2.4,
      strokeColor: '#0b62d6',
      strokeOpacity: 1,
      fillColor: '#0b62d6',
      fillOpacity: 1
    };

    // Draw one arrow in the middle of each leg
    const segmentArrows = [];
    for (let i = 0; i < path.length - 1; i++) {
      const leg = new google.maps.Polyline({
        path: [path[i], path[i + 1]],
        geodesic: true,
        strokeOpacity: 0, // invisible line
        icons: [{ icon: arrowSymbol, offset: '50%' }]
      });
      leg.setMap(map);
      segmentArrows.push(leg);
    }

    // ---------------------------------------------------------------
    // Optional ship animation
    // ---------------------------------------------------------------
    let raf = null;
    if (animate && path.length > 1) {
      const segDist = [];
      let total = 0;
      for (let i = 0; i < path.length - 1; i++) {
        const d = google.maps.geometry.spherical.computeDistanceBetween(path[i], path[i + 1]);
        segDist.push(d);
        total += d;
      }

      if (total > 0) {
        const shipIcon = {
          path: 'M0,-12 L6,-4 L3,-4 L3,6 L-3,6 L-3,-4 L-6,-4 Z',
          scale: 1.6,
          fillColor: '#0b62d6',
          fillOpacity: 1,
          strokeWeight: 1.25,
          strokeColor: '#ffffff',
          anchor: new google.maps.Point(0, 0),
          rotation: 0
        };

        const ship = new google.maps.Marker({
          position: path[0],
          map,
          icon: shipIcon,
          zIndex: 9999
        });

        const mps = metersPerSecond(shipSpeedKmh);
        const EPS_METERS = Math.max(3000, total * 0.01);
        const MIN_S = EPS_METERS;
        const MAX_S = Math.max(EPS_METERS, total - EPS_METERS);

        let traveled = MIN_S;
        let lastTs = null;

        function latLngAt(sMeters) {
          sMeters = Math.max(0, Math.min(sMeters, total));
          if (sMeters <= 0) return path[0];
          if (sMeters >= total) return path[path.length - 1];
          let acc = 0;
          for (let i = 0; i < segDist.length; i++) {
            const d = segDist[i];
            if (acc + d >= sMeters) {
              const remain = sMeters - acc;
              const heading = google.maps.geometry.spherical.computeHeading(path[i], path[i + 1]);
              return google.maps.geometry.spherical.computeOffset(path[i], remain, heading);
            }
            acc += d;
          }
          return path[path.length - 1];
        }

        function step(ts) {
          if (lastTs == null) lastTs = ts;
          const dt = (ts - lastTs) / 1000;
          lastTs = ts;
          traveled += mps * dt;

          if (traveled >= MAX_S) traveled = MIN_S;

          const pos = latLngAt(traveled);
          ship.setPosition(pos);

          const ahead = latLngAt(Math.min(traveled + 30, total));
          const hdg = google.maps.geometry.spherical.computeHeading(pos, ahead);
          const icon = ship.getIcon();
          icon.rotation = hdg;
          ship.setIcon(icon);

          raf = requestAnimationFrame(step);
        }

        raf = requestAnimationFrame(step);
      }
    }

    return {
      map,
      baseRoute,
      segmentArrows,
      destroy: () => {
        if (raf) cancelAnimationFrame(raf);
        baseRoute.setMap(null);
        segmentArrows.forEach(a => a.setMap(null));
      }
    };
  }

  return { render };
})();
